// Knowledge-base document manager: upload (click or drag-drop), ingestion status
// with polling while documents are pending/processing, delete and re-ingest.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  IconDownload,
  IconEye,
  IconFile,
  IconRefresh,
  IconWorld,
  IconX,
} from "@tabler/icons-react";
import {
  addUrlSource,
  deleteDocument,
  downloadDocument,
  getDocumentChunks,
  listDocuments,
  reingestDocument,
  uploadDocument,
} from "../../services/skills";
import type { KnowledgeChunk, SkillDocument } from "../../types/skill";

const POLL_MS = 2500;
const ACCEPT = ".pdf,.docx,.txt,.md,.markdown";

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function statusIcon(status: SkillDocument["status"]): string {
  switch (status) {
    case "ready":
      return "✅";
    case "failed":
      return "❌";
    case "processing":
      return "⚙️";
    default:
      return "⏳";
  }
}

export function DocumentManager({
  skillId,
  onError,
}: {
  skillId: string;
  onError: (message: string) => void;
}) {
  const [documents, setDocuments] = useState<SkillDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [chunks, setChunks] = useState<KnowledgeChunk[] | null>(null);
  const [url, setUrl] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      setDocuments(await listDocuments(skillId));
    } catch (e) {
      onError(`Failed to load documents: ${e instanceof Error ? e.message : e}`);
    }
  }, [skillId, onError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll while any document is still ingesting.
  const ingesting = documents.some((d) => d.status === "pending" || d.status === "processing");
  useEffect(() => {
    if (!ingesting) return;
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [ingesting, refresh]);

  async function handleFiles(files: FileList | File[]) {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadDocument(skillId, file);
      }
    } catch (e) {
      onError(`Upload failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setUploading(false);
      refresh();
    }
  }

  async function remove(doc: SkillDocument) {
    if (!confirm(`Delete "${doc.filename}" and its ${doc.chunkCount} chunks?`)) return;
    try {
      await deleteDocument(skillId, doc.id);
    } catch (e) {
      onError(`Delete failed: ${e instanceof Error ? e.message : e}`);
    }
    refresh();
  }

  async function reingest(doc: SkillDocument) {
    try {
      await reingestDocument(skillId, doc.id);
    } catch (e) {
      onError(`Re-ingest failed: ${e instanceof Error ? e.message : e}`);
    }
    refresh();
  }

  async function addUrl() {
    const clean = url.trim();
    if (!clean || addingUrl) return;
    setAddingUrl(true);
    try {
      await addUrlSource(skillId, clean);
      setUrl("");
    } catch (e) {
      onError(`Add web page failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setAddingUrl(false);
      refresh();
    }
  }

  async function toggleView(doc: SkillDocument) {
    if (viewingId === doc.id) {
      setViewingId(null);
      setChunks(null);
      return;
    }
    setViewingId(doc.id);
    setChunks(null);
    try {
      setChunks(await getDocumentChunks(skillId, doc.id));
    } catch (e) {
      setViewingId(null);
      onError(`Failed to load chunks: ${e instanceof Error ? e.message : e}`);
    }
  }

  async function download(doc: SkillDocument) {
    try {
      const blob = await downloadDocument(skillId, doc.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(`Download failed: ${e instanceof Error ? e.message : e}`);
    }
  }

  return (
    <div>
      <div
        className={`dropzone${dragOver ? " drag-over" : ""}`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
        }}
      >
        {uploading
          ? "Uploading…"
          : "Drop PDF / DOCX / TXT / MD files here, or click to browse (max 20 MB each)"}
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      <div className="url-source-row">
        <IconWorld size={16} stroke={1.75} className="muted" />
        <input
          type="url"
          placeholder="https://… add a web page (scraped via Firecrawl)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addUrl()}
        />
        <button className="btn btn-sm" onClick={addUrl} disabled={addingUrl || !url.trim()}>
          {addingUrl ? "…" : "+ Add page"}
        </button>
      </div>

      {documents.length > 0 && (
        <table className="guide-table doc-table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Status</th>
              <th>Chunks</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id}>
                <td>
                  <div className="name doc-name">
                    {doc.sourceType === "url" ? (
                      <IconWorld size={14} stroke={1.75} />
                    ) : (
                      <IconFile size={14} stroke={1.75} />
                    )}
                    {doc.filename}
                  </div>
                  <div className="role">
                    {doc.sourceType === "url" && doc.sourceUrl ? (
                      <a href={doc.sourceUrl} target="_blank" rel="noreferrer">
                        {doc.sourceUrl}
                      </a>
                    ) : (
                      formatSize(doc.sizeBytes)
                    )}
                  </div>
                </td>
                <td>
                  <span title={doc.error || undefined}>
                    {statusIcon(doc.status)} {doc.status}
                  </span>
                  {doc.status === "failed" && doc.error && (
                    <div className="role error-text">{doc.error}</div>
                  )}
                </td>
                <td className="voice-cell">{doc.chunkCount || "—"}</td>
                <td className="voice-cell">
                  <button
                    className={`btn btn-sm${viewingId === doc.id ? " active" : ""}`}
                    title="Review the ingested chunks"
                    onClick={() => toggleView(doc)}
                    disabled={doc.status !== "ready"}
                  >
                    <IconEye size={15} stroke={1.75} />
                  </button>{" "}
                  <button
                    className="btn btn-sm"
                    title="Download the original file"
                    onClick={() => download(doc)}
                  >
                    <IconDownload size={15} stroke={1.75} />
                  </button>{" "}
                  <button
                    className="btn btn-sm"
                    title="Re-run extraction, chunking and embedding from the stored original"
                    onClick={() => reingest(doc)}
                    disabled={doc.status === "processing" || doc.status === "pending"}
                  >
                    <IconRefresh size={15} stroke={1.75} />
                  </button>{" "}
                  <button
                    className="btn btn-sm btn-danger"
                    title="Delete document and its chunks"
                    onClick={() => remove(doc)}
                  >
                    <IconX size={15} stroke={1.75} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {viewingId && (
        <ChunkReviewModal
          document={documents.find((d) => d.id === viewingId)}
          chunks={chunks}
          onClose={() => {
            setViewingId(null);
            setChunks(null);
          }}
        />
      )}
    </div>
  );
}

function ChunkReviewModal({
  document: doc,
  chunks,
  onClose,
}: {
  document: SkillDocument | undefined;
  chunks: KnowledgeChunk[] | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div>
            <strong>{doc?.filename || "Document"}</strong>
            <div className="muted modal-sub">
              {chunks
                ? `${chunks.length} ingested chunk${chunks.length === 1 ? "" : "s"}`
                : "Loading chunks…"}
              {doc?.sourceUrl ? (
                <>
                  {" · "}
                  <a href={doc.sourceUrl} target="_blank" rel="noreferrer">source</a>
                </>
              ) : null}
            </div>
          </div>
          <div className="spacer" />
          <button className="btn btn-sm" onClick={onClose} aria-label="Close">
            <IconX size={15} stroke={1.75} />
          </button>
        </div>
        <div className="modal-body">
          {chunks?.map((chunk) => (
            <div className="search-hit" key={chunk.id}>
              <div className="search-hit-head">
                <span className="mono">#{chunk.chunkIndex}</span>
                {chunk.heading && <span className="muted">{chunk.heading}</span>}
              </div>
              <div className="search-hit-body">{chunk.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
